import "./App.css";
import "./i18n";
import "moment/locale/he";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Routers from "./config/Routers";
import rootReducer from "./store";
import { Provider } from "react-redux";
import ApiProvider from "./utils/ApiProvider";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { heIL } from "@mui/x-date-pickers/locales";
import { heIL as coreHeIL } from "@mui/material/locale";
import { getFromLocalStorage } from "config/functions";
import { BusinessTypeProvider } from "hooks/useBizTypeTranslation";
import { registerServiceWorker } from "registerServiceWorker";
import { useEffect, useMemo, useState } from "react";

function App() {
  const [primaryColor, setPrimaryColor] = useState({ main: "#000", text: "#fff" });
  const [secondaryColor, setSecondaryColor] = useState({ main: "#000", text: "#fff" });
  const [paletteMode, setPaletteMode] = useState('light');
  const user = getFromLocalStorage("user");

  const cacheRtl = useMemo(() =>
    createCache({
      key: user?.lang?.dir === "ltr" ? "mybee-ltr" : "mybee-rtl",
      stylisPlugins: user?.lang?.dir === "ltr" ? [prefixer] : [prefixer, rtlPlugin],
    }),
    [user]
  );

  const theme = createTheme(
    {
      direction: user?.lang ? user?.lang?.dir : "rtl",
      typography: {
        fontFamily: ["Assistant", '"Segoe UI"', "Roboto"].join(","),
        allVariants: { color: primaryColor.text },
        body2: {
          fontSize: 12,
        },
        h6: {
          fontSize: 17,
          fontWeight: 600,
        },
        h5: {
          fontSize: 21,
          fontWeight: 700,
        },
        h4: {
          fontSize: 24,
          fontWeight: 700,
        },
        h3: {
          fontSize: 28,
          fontWeight: 700,
        },
      },
      fonts: {
        body: "heebo,system-ui, sans-serif",
        heading: 'heebo, "Avenir Next", sans-serif',
        monospace: "heebo, Menlo, monospace",
      },
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 7,
              "&:hover fieldset": {
                borderColor: primaryColor.text,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor.text,
              },

              "&.Mui-focused": {
                borderColor: primaryColor.text,
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E02020",
                color: primaryColor.text
              },
              "&.Mui-error svg": {
                color: "#E02020",
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: primaryColor.text, // צבע ברירת מחדל
              "&.Mui-focused": {
                color: primaryColor.text,
              },
              "&.Mui-error": {
                color: "#E02020",
              },
            },
          },
        },
        MuiTextField: {
          defaultProps: { size: "small" },
          styleOverrides: {
            root: {
              "& input[type='number']::-webkit-outer-spin-button, & input[type='number']::-webkit-inner-spin-button": {
                WebkitAppearance: "none", // Chrome, Edge
                margin: 0,
              },
              "& input[type='number']": {
                MozAppearance: "textfield", // Firefox
              },
            },
          },
        },
        MuiButton: {
          defaultProps: {
            size: "small",
            variant: "contained",
          },
          styleOverrides: {
            root: {
              backgroundColor: primaryColor.main,
              textTransform: "none",
              borderRadius: 8,
              border: 0,
              "&:hover": {
                backgroundColor: primaryColor.main,
                opacity: 0.9,
                border: 0,
              },
              "&:disabled": {
                backgroundColor: "#cececeff",
                color: "#dad8d8ff",
              },
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              color: primaryColor.text,
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              borderRadius: 10,
              boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
            },
          },
        },

        MuiTimePickerToolbar: {
          styleOverrides: {
            hourMinuteLabel: {
              '& .MuiTypography-h3': {
                backgroundColor: "#fff",
                color: primaryColor.text,
                fontSize: 26,
                fontWeight: 200
              },
            },
            separator: { color: '#fff' },
          },
        },

        MuiDateTimePicker: {
          defaultProps: {
            slotProps: {
              toolbar: {
                sx: {
                  '& .MuiTypography-h3': {
                    backgroundColor: '#fff',
                    color: primaryColor.text,
                    fontSize: 26,
                    fontWeight: 200,
                    px: 1,
                  },
                  '& .MuiTypography-h4': {
                    backgroundColor: '#fff',
                    color: primaryColor.text,
                    fontSize: 26,
                    fontWeight: 200,
                    px: 1,
                  },
                  '& .MuiPickersToolbar-separator': { color: '#fff' },
                },
              },
            },
          },
        },
      },
      palette: {
        mode: paletteMode,
        primary: primaryColor,
        secondary: secondaryColor,

        bgPage: {
          main: paletteMode === "dark" ? "#121212" : "#F5F5F5",
        },
        bg: {
          main: paletteMode === "dark" ? "#1E1E1E" : "#FFFFFF",
          borderBottom: paletteMode === "dark" ? "#333333" : "#E3E3E3",
        },
        bgDivider: { main: "#E3E3E3" },
        success: {
          main: paletteMode === "dark" ? "#0EA77A" : "#13BE90",
          text: paletteMode === "dark" ? "#E8FFF7" : "#FFFFFF",
        },
        warning: {
          main: paletteMode === "dark" ? "#fff2d9" : "#ffaa01",
          text: paletteMode === "dark" ? "#ffaa01" : "#fff2d9",
          light: paletteMode === "dark" ? "#ffaa01" : "#fce9c5",
        },
        info: {
          main: paletteMode === "dark" ? "#90caf9" : "#1587e4",
          text: paletteMode === "dark" ? "#e3f2fd" : "#e3f2fd",
        },
        error: {
          main: paletteMode === "dark" ? "#B00020" : "#FFE1D9",
          text: paletteMode === "dark" ? "#FFDADA" : "#E02020",
        },
      },
      direction: user?.lang ? user?.lang?.dir : "rtl",
    },
    heIL,
    coreHeIL
  );

  useEffect(() => {
    registerServiceWorker()

    console.info(
      "%c MyBee ",
      "color: #13BE90; background: #033841; font-size: 42px; font-weight: bold; padding: 8px 20px; border-radius: 8px;"
    );

    console.info(
      "%cSmart IoT & Energy ⚡",
      "color: #13BE90; background: #033841; font-size: 18px; font-weight: bold; padding: 8px 20px; border-radius: 8px;"
    );

    console.info(
      "%c🐝 Curious minds are welcome here 👀",
      "color: #151515ff; font-size: 16px;"
    );
  }, []);


  return (
    <ThemeProvider theme={theme}>
      <Provider store={rootReducer}>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="he">
          <BusinessTypeProvider>
            <CacheProvider value={cacheRtl}>
              <ApiProvider>
                <Routers
                  setPaletteMode={setPaletteMode}
                  setPrimaryColor={setPrimaryColor}
                  setSecondaryColor={setSecondaryColor}
                />
              </ApiProvider>
            </CacheProvider>
          </BusinessTypeProvider>
        </LocalizationProvider>
      </Provider>
    </ThemeProvider>
  );
}

export default App;
